import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Card, CardContent } from '../../components/ui/card';
import { toast } from 'sonner';
import { Search, Receipt, Printer, RotateCcw, Ban, Calendar, CreditCard, Banknote } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TransactionsPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [dateFilter, setDateFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [operatorFilter, setOperatorFilter] = useState('all');
  const [operators, setOperators] = useState([]);
  const [selectedSale, setSelectedSale] = useState(null);
  const [loading, setLoading] = useState(false);
  const limit = 20;

  useEffect(() => {
    fetchTransactions();
  }, [offset, dateFilter, paymentFilter, operatorFilter]);

  useEffect(() => {
    fetchOperators();
  }, []);

  const fetchOperators = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setOperators(response.data);
    } catch (error) {
      console.error('Error fetching operators:', error);
    }
  };

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      let url = `${API}/transactions?limit=${limit}&offset=${offset}`;
      if (dateFilter) url += `&date=${dateFilter}`;
      if (paymentFilter && paymentFilter !== 'all') url += `&payment_method=${paymentFilter}`;
      if (operatorFilter && operatorFilter !== 'all') url += `&operator_id=${operatorFilter}`;
      
      const response = await axios.get(url);
      setTransactions(response.data.transactions);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleRefund = async (saleId) => {
    const reason = prompt('Enter refund reason:');
    if (!reason) return;
    
    try {
      await axios.post(`${API}/sales/${saleId}/refund`, { sale_id: saleId, reason });
      toast.success('Sale refunded');
      fetchTransactions();
      setSelectedSale(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to refund');
    }
  };

  const handleVoid = async (saleId) => {
    const reason = prompt('Enter void reason:');
    if (!reason) return;
    
    try {
      await axios.post(`${API}/sales/${saleId}/void?reason=${encodeURIComponent(reason)}`);
      toast.success('Sale voided');
      fetchTransactions();
      setSelectedSale(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to void');
    }
  };

  const handlePrintReceipt = async (saleId) => {
    try {
      const response = await axios.get(`${API}/receipts/${saleId}`);
      const receipt = response.data;
      
      const printWindow = window.open('', '_blank', 'width=400,height=600');
      printWindow.document.write(`
        <html>
          <head>
            <title>Receipt - ${receipt.receipt_number}</title>
            <style>
              body { font-family: 'Courier New', monospace; font-size: 12px; padding: 20px; max-width: 300px; margin: 0 auto; }
              .center { text-align: center; }
              .bold { font-weight: bold; }
              .line { border-top: 1px dashed #000; margin: 10px 0; }
              .item { display: flex; justify-content: space-between; margin: 4px 0; }
              .total { font-size: 14px; font-weight: bold; }
              @media print { body { padding: 0; } }
            </style>
          </head>
          <body>
            <div class="center bold" style="font-size: 16px;">${receipt.business_name.toUpperCase()}</div>
            ${receipt.vat_number ? `<div class="center">VAT: ${receipt.vat_number}</div>` : ''}
            <div class="line"></div>
            <div>Receipt: ${receipt.receipt_number}</div>
            <div>Order #: ${receipt.order_number}</div>
            <div>Date: ${new Date(receipt.date).toLocaleString()}</div>
            <div>Operator: ${receipt.operator}</div>
            <div class="line"></div>
            ${receipt.items.map(item => `
              <div class="item">
                <span>${item.quantity}x ${item.product_name}</span>
                <span>£${(item.quantity * (item.unit_price_inc_vat || item.unit_price)).toFixed(2)}</span>
              </div>
            `).join('')}
            <div class="line"></div>
            <div class="item"><span>Subtotal</span><span>£${receipt.subtotal.toFixed(2)}</span></div>
            <div class="item"><span>VAT @ ${receipt.vat_rate}%</span><span>£${receipt.vat_total.toFixed(2)}</span></div>
            <div class="item total"><span>TOTAL</span><span>£${receipt.total.toFixed(2)}</span></div>
            <div class="line"></div>
            <div class="center">Payment: ${receipt.payment_method.toUpperCase()}</div>
            <div class="center" style="margin-top: 10px;">VAT RECEIPT</div>
            <div class="center">${receipt.footer_message}</div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 250);
    } catch (error) {
      toast.error('Failed to print receipt');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'refunded': return 'bg-amber-100 text-amber-700';
      case 'voided': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getPaymentIcon = (method) => {
    switch (method) {
      case 'card': return <CreditCard className="w-4 h-4" />;
      case 'cash': return <Banknote className="w-4 h-4" />;
      case 'account': return <Receipt className="w-4 h-4" />;
      default: return <CreditCard className="w-4 h-4" />;
    }
  };

  return (
    <div data-testid="transactions-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Transaction Log</h1>
          <p className="text-slate-500 mt-1 font-body">View and manage all sales transactions</p>
        </div>
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-400" />
          <Input
            type="date"
            data-testid="date-filter"
            value={dateFilter}
            onChange={(e) => { setDateFilter(e.target.value); setOffset(0); }}
            className="w-40"
          />
        </div>
        
        <Select value={paymentFilter} onValueChange={(v) => { setPaymentFilter(v); setOffset(0); }}>
          <SelectTrigger className="w-40" data-testid="payment-filter">
            <SelectValue placeholder="All payments" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All payments</SelectItem>
            <SelectItem value="card">Card</SelectItem>
            <SelectItem value="cash">Cash</SelectItem>
            <SelectItem value="account">Account</SelectItem>
          </SelectContent>
        </Select>

        <Select value={operatorFilter} onValueChange={(v) => { setOperatorFilter(v); setOffset(0); }}>
          <SelectTrigger className="w-40" data-testid="operator-filter">
            <SelectValue placeholder="All operators" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All operators</SelectItem>
            {operators.map((op) => (
              <SelectItem key={op.id} value={op.id}>{op.username}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {(dateFilter || paymentFilter !== 'all' || operatorFilter !== 'all') && (
          <Button variant="ghost" size="sm" onClick={() => { setDateFilter(''); setPaymentFilter('all'); setOperatorFilter('all'); }}>
            Clear filters
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="data-table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Date/Time</th>
                <th>Operator</th>
                <th>Items</th>
                <th>Payment</th>
                <th>Total</th>
                <th>Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-slate-400">Loading...</td>
                </tr>
              ) : transactions.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-slate-400">No transactions found</td>
                </tr>
              ) : (
                transactions.map((tx) => (
                  <tr key={tx.id} data-testid={`tx-row-${tx.id}`}>
                    <td className="font-mono font-medium">#{tx.order_number || 'N/A'}</td>
                    <td className="text-sm">
                      {new Date(tx.created_at).toLocaleDateString()}<br/>
                      <span className="text-slate-400">{new Date(tx.created_at).toLocaleTimeString()}</span>
                    </td>
                    <td>{tx.operator_name}</td>
                    <td className="text-sm">
                      {tx.items.slice(0, 2).map((i, idx) => (
                        <div key={idx}>{i.quantity}x {i.product_name}</div>
                      ))}
                      {tx.items.length > 2 && <div className="text-slate-400">+{tx.items.length - 2} more</div>}
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        {getPaymentIcon(tx.payment_method)}
                        <span className="capitalize text-sm">{tx.payment_method}</span>
                      </div>
                    </td>
                    <td className="price-display">£{tx.total.toFixed(2)}</td>
                    <td>
                      <span className={`text-xs px-2 py-1 rounded capitalize ${getStatusColor(tx.status)}`}>
                        {tx.status}
                      </span>
                    </td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedSale(tx)}
                          data-testid={`view-tx-${tx.id}`}
                        >
                          <Search className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePrintReceipt(tx.id)}
                          data-testid={`print-tx-${tx.id}`}
                        >
                          <Printer className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          Showing {offset + 1} - {Math.min(offset + limit, total)} of {total} transactions
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - limit))}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={offset + limit >= total}
            onClick={() => setOffset(offset + limit)}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Transaction Detail Modal */}
      <Dialog open={!!selectedSale} onOpenChange={() => setSelectedSale(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="tx-detail-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              Transaction #{selectedSale?.order_number || 'N/A'}
            </DialogTitle>
            <DialogDescription className="sr-only">Transaction details</DialogDescription>
          </DialogHeader>
          
          {selectedSale && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Date/Time</p>
                  <p className="font-medium">{new Date(selectedSale.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-slate-500">Operator</p>
                  <p className="font-medium">{selectedSale.operator_name}</p>
                </div>
                <div>
                  <p className="text-slate-500">Payment Method</p>
                  <p className="font-medium capitalize">{selectedSale.payment_method}</p>
                </div>
                <div>
                  <p className="text-slate-500">Status</p>
                  <span className={`text-xs px-2 py-1 rounded capitalize ${getStatusColor(selectedSale.status)}`}>
                    {selectedSale.status}
                  </span>
                </div>
              </div>

              {selectedSale.account_signatory && (
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="text-sm text-purple-700">
                    <strong>Account Signatory:</strong> {selectedSale.account_signatory}
                  </p>
                </div>
              )}

              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-2">Items</h4>
                <div className="space-y-2">
                  {selectedSale.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span>{item.quantity}x {item.product_name}</span>
                      <span>£{(item.quantity * item.unit_price).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t mt-3 pt-3 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal</span>
                    <span>£{(selectedSale.subtotal || selectedSale.total).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>VAT</span>
                    <span>£{(selectedSale.vat_total || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-bold">
                    <span>Total</span>
                    <span>£{selectedSale.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => handlePrintReceipt(selectedSale.id)}
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Reprint Receipt
                </Button>
                {selectedSale.status === 'completed' && (
                  <>
                    <Button
                      variant="outline"
                      className="text-amber-600"
                      onClick={() => handleRefund(selectedSale.id)}
                      data-testid="refund-btn"
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Refund
                    </Button>
                    <Button
                      variant="outline"
                      className="text-red-600"
                      onClick={() => handleVoid(selectedSale.id)}
                      data-testid="void-btn"
                    >
                      <Ban className="w-4 h-4 mr-1" />
                      Void
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransactionsPage;
